import { trpcClient, trpcClientUnbatched } from '@web-app/utils/trpc';
import { molecule } from 'bunshi';
import { parseISO } from 'date-fns';
import { atom } from 'jotai';
import { atomWithInfiniteQuery, atomWithQuery } from 'jotai-tanstack-query';

import { sessionMolecule } from './sessionMolecule';

const STALE_TIME = 5 * 60 * 1000;

// ============================================================================
// Journal Entries Molecule
// ============================================================================

/**
 * Journal Entries Molecule
 *
 * Provides all journal entry atoms scoped to a session (keyed by roomId).
 * This molecule is automatically created and destroyed when entering/leaving a session.
 *
 * Depends on SessionMolecule for patientIdAtom.
 *
 * Includes atoms for:
 * - Journal entries fetching and pagination
 * - Last appointment tracking
 * - Journal entries since last appointment
 *
 * The atoms within this molecule:
 * - `lastAppointmentQueryAtom`: Fetches the last appointment for the patient
 * - `lastAppointmentAtom`: Extracted last appointment data
 * - `lastAppointmentDateAtom`: Extracted last appointment date string
 * - `journalEntriesPageSizeAtom`: Configurable page size (default 50)
 * - `journalEntriesQueryAtom`: Infinite query for fetching journal entries
 * - `journalEntriesArrayAtom`: Flattened array of all loaded entries
 * - `journalEntriesSinceLastAppointmentAtom`: Count of entries since last appointment
 */
export const journalEntriesMolecule = molecule((mol) => {
  const { patientIdAtom } = mol(sessionMolecule);

  // ============================================================================
  // LAST APPOINTMENT ATOMS
  // ============================================================================

  /**
   * Query atom that fetches the last occurred appointment for the current patient.
   * Enabled only when patientId is available.
   */
  const lastAppointmentQueryAtom = atomWithQuery((get) => {
    const patientId = get(patientIdAtom);

    return {
      queryKey: ['sessionPrep.lastAppointment', patientId],
      queryFn: async () => {
        if (!patientId) return null;
        return trpcClientUnbatched.sessionPrep.getLastAppointment.query({
          patientId,
        });
      },
      enabled: !!patientId,
      staleTime: STALE_TIME,
      refetchOnWindowFocus: false,
    };
  });

  /**
   * Extracts the last appointment data (id + date) from the query result.
   * Returns null if not available.
   */
  const lastAppointmentAtom = atom((get) => {
    const queryResult = get(lastAppointmentQueryAtom);
    return queryResult.data ?? null;
  });

  /**
   * Extracts just the last appointment date as an ISO date string (YYYY-MM-DD).
   * Healthie returns dates in "YYYY-MM-DD HH:mm:ss Z" format, so we extract
   * only the date portion for consistent parsing with parseISO / date-fns.
   * Returns null if not available.
   */
  const lastAppointmentDateAtom = atom((get) => {
    const lastAppointment = get(lastAppointmentAtom);
    const rawDate = lastAppointment?.date;
    if (!rawDate) return null;
    // Extract YYYY-MM-DD from Healthie's "YYYY-MM-DD HH:mm:ss Z" format
    const dateOnly = rawDate.split(' ')[0];
    return dateOnly;
  });

  // ============================================================================
  // JOURNAL ENTRIES ATOMS
  // ============================================================================

  /**
   * Configurable page size for journal entries pagination.
   * Default: 50 entries per page
   */
  const journalEntriesPageSizeAtom = atom<number>(50);

  /**
   * Infinite query atom for fetching journal entries from Platform API via tRPC.
   * Uses jotai-tanstack-query atomWithInfiniteQuery for automatic page accumulation.
   *
   * Returns InfiniteQueryObserverResult with data.pages, fetchNextPage, hasNextPage, etc.
   * Enabled only when patientId is available.
   */
  const journalEntriesQueryAtom = atomWithInfiniteQuery((get) => {
    const patientId = get(patientIdAtom);
    const pageSize = get(journalEntriesPageSizeAtom);

    return {
      queryKey: ['journalEntries', 'byPatientId', patientId, pageSize],
      queryFn: async ({ pageParam }: { pageParam: number }) => {
        if (!patientId) {
          return { entries: [], pageInfo: { hasMore: false } };
        }
        return trpcClient.journalEntries.getByPatientId.query({
          patientId,
          pageSize,
          offset: pageParam,
        });
      },
      initialPageParam: 0,
      getNextPageParam: (
        lastPage: { pageInfo: { hasMore: boolean } },
        _allPages: unknown[],
        lastPageParam: number,
      ) => (lastPage.pageInfo.hasMore ? lastPageParam + pageSize : undefined),
      enabled: !!patientId,
      staleTime: 5 * 60 * 1000,
      retry: 3,
      retryDelay: 10 * 1000,
      refetchOnWindowFocus: false,
    };
  });

  /**
   * Derived atom that extracts and flattens entries from all pages.
   * Returns empty array if query hasn't run or failed.
   */
  const journalEntriesArrayAtom = atom((get) => {
    const queryResult = get(journalEntriesQueryAtom);
    return queryResult.data?.pages.flatMap((page) => page.entries) ?? [];
  });

  // ============================================================================
  // JOURNAL ENTRIES SINCE LAST APPOINTMENT
  // ============================================================================

  /**
   * Derived atom that counts journal entries created since the last appointment.
   *
   * Returns:
   * - null if last appointment date or journal entries are not yet available
   * - { count, hasMore } where hasMore is true when entries fill the page
   *   (indicating there may be more entries not yet loaded)
   */
  const journalEntriesSinceLastAppointmentAtom = atom((get) => {
    const lastDate = get(lastAppointmentDateAtom);
    const queryResult = get(journalEntriesQueryAtom);
    const entries = get(journalEntriesArrayAtom);
    const hasMore = queryResult.hasNextPage;
    const pageSize = get(journalEntriesPageSizeAtom);

    if (!lastDate || queryResult.status === 'pending') {
      return null;
    }

    const appointmentDate = parseISO(lastDate);
    const count = entries.filter((entry: { createdAt: string }) => {
      const entryDate = parseISO(entry.createdAt);
      return entryDate > appointmentDate;
    }).length;

    return {
      count,
      hasMore: hasMore && count >= pageSize,
    };
  });

  return {
    lastAppointmentQueryAtom,
    lastAppointmentAtom,
    lastAppointmentDateAtom,
    journalEntriesPageSizeAtom,
    journalEntriesQueryAtom,
    journalEntriesArrayAtom,
    journalEntriesSinceLastAppointmentAtom,
  };
});
